import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Typography,
  toast,
} from "@altimateai/lego";
import { Stack, LoadingButton } from "@altimateai/lego";
import { DataTable } from "../../components/tables";
import { TableHeaderType, TableRowItem } from "../../components/tables/types";
import { getCompanyList, addNewCompany } from "../../helpers/apis";
import { CompanyListItem } from "../../helpers/types";

type CompanyItem = CompanyListItem & TableRowItem;

const CompanyList = () => {
  const { data } = useQuery({
    queryKey: ["get-company-list"],
    queryFn: () => getCompanyList(),
  });
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [company, setCompany] = useState("");
  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: () =>
      addNewCompany({
        name: company,
        country: "USA",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get-company-list"] });
      setShowModal(false);
      setCompany("");
      toast({ description: "Company added successfully" });
    },
    onError: (error: Error) => {
      toast({
        description: `Failed to add company: ${error || "Unknown error"}`,
        variant: "destructive",
      });
    },
  });

  const header: TableHeaderType<CompanyItem>[] = [
    {
      id: 1,
      label: "Name",
      uid: "name",
      width: "50%",
      render: item => <div className="al-cursor-pointer">{item.name}</div>,
    },
    {
      id: 2,
      label: "Country",
      uid: "country",
      width: "50%",
      render: item => <div>{item.country}</div>,
    },
  ];

  return (
    <>
      <Card>
        <CardContent className="al-p-4">
          <Stack direction="row" justify="between" align="center" className="al-m-4">
            <Typography variant="page-heading">Companies</Typography>
            <Button
              variant="default"
              onClick={e => {
                e.preventDefault();
                setShowModal(true);
              }}
            >
              Add Company
            </Button>
          </Stack>
          {data?.items?.length ? (
            <DataTable<CompanyItem>
              header={header}
              data={{ items: data.items }}
              getRowUrl={item => `/settings/companies/${item.name}`}
              getRowState={item => ({ entityName: item.name })}
            />
          ) : (
            <div className="al-text-center al-py-8 al-text-gray-500">No companies found</div>
          )}
        </CardContent>
      </Card>
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Company</DialogTitle>
          </DialogHeader>
          <div className="al-space-y-2">
            <Label htmlFor="company">Company</Label>
            <Input
              id="company"
              value={company}
              onChange={e => setCompany(e.target.value)}
              placeholder="Enter company name"
              minLength={1}
              maxLength={256}
            />
          </div>
          <Stack direction="row" justify="end" gap="sm" className="al-mt-4">
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={isLoading}>
              Cancel
            </Button>
            <LoadingButton onClick={() => mutate()} disabled={!company.trim()} loading={isLoading}>
              {isLoading ? "Adding..." : "Submit"}
            </LoadingButton>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export { CompanyList };
